import React, { useMemo, useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, ToggleButton, ToggleButtonGroup, Table, TableHead, TableRow, TableCell, TableBody, Alert } from "@mui/material";
import dayjs from "dayjs";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";

function parseBool(v) {
    if (v == null || v === "") return null;
    const s = String(v).trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(s)) return true;
    if (["false", "0", "no", "n"].includes(s)) return false;
    return "INVALID";
}

function parsePasted(text, separator, companyKey) {
    const sep = separator === "tab" ? "\t" : separator; // "," eller ";"
    const lines = (text ?? "").split(/\r?\n/);

    const rows = [];
    const errors = [];

    lines.forEach((line, idx) => {
        const rowNo = idx + 1;
        const trimmed = line.trim();
        if (!trimmed) return;
        if (trimmed.startsWith("#")) return;

        const cols = line.split(sep).map((c) => c.trim());

        const name = cols[0];
        const description = cols[1];

        rows.push({
            name,
            description,
            companyKey,
        });
    });

    // dedupe: senaste vinner
    const map = new Map();
    rows.forEach((r) => map.set(r.name, r));
    const deduped = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));

    return { rows: deduped, errors };
}

export default function PasteImportToolDialog({
    open,
    onClose,
    onImport,          // async (items) => void
    isImporting,
    paperSx,
}) {
    const [separator, setSeparator] = useState("tab"); // tab | , | ;
    const [text, setText] = useState("");
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);

    const { formatMessage } = useIntl();

    const parsed = useMemo(() => parsePasted(text, separator, companyKey), [text, separator, companyKey]);

    const handleImport = async () => {
        if (parsed.errors.length > 0) return;
        if (parsed.rows.length === 0) return;

        try {
            await onImport(parsed.rows);
            setText("");
            onClose();
        } catch {
            // Låt parent visa felmeddelande.
            // Behåll texten så användaren kan försöka igen.
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: paperSx }}>
            <DialogTitle><FormattedMessage {...messages.pasteToolDialogTitle} /></DialogTitle>

            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                            <FormattedMessage
                                {...messages.pasteToolHelpText}
                                values={{
                                    br: () => <br />,
                                    b: chunks => <b>{chunks}</b>,
                                    code: chunks => <code>{chunks}</code>,
                                }}
                            />
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Typography variant="body2" sx={{ minWidth: 90 }}><FormattedMessage {...messages.separatorLabel} /></Typography>

                        <ToggleButtonGroup
                            exclusive
                            value={separator}
                            onChange={(_, v) => v && setSeparator(v)}
                            size="small"
                        >
                            <ToggleButton value="tab">Tab</ToggleButton>
                            <ToggleButton value=",">,</ToggleButton>
                            <ToggleButton value=";">;</ToggleButton>
                        </ToggleButtonGroup>

                        <Typography variant="body2" sx={{ opacity: 0.7 }}>
                            <FormattedMessage {...messages.pasteToolHelpText2} />
                        </Typography>
                    </Box>

                    <TextField
                        label={formatMessage(messages.toolTextFieldLabel)}
                        multiline
                        minRows={7}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        fullWidth
                    />

                    {parsed.errors.length > 0 && (
                        <Alert severity="error">
                            {parsed.errors.slice(0, 5).map((e, i) => (
                                <div key={i}><FormattedMessage {...messages.rowError} values={{ rowNo: e.rowNo, message: e.message }} /></div>
                            ))}
                            {parsed.errors.length > 5 && <div><FormattedMessage {...messages.moreErrors} values={{ count: parsed.errors.length - 5 }} /></div>}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            <FormattedMessage {...messages.previewTitle} values={{ rowCount: parsed.rows.length }} />
                        </Typography>

                        <Box sx={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: 1, overflow: "hidden" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><FormattedMessage {...messages.nameColumnLabel} /></TableCell>
                                        <TableCell><FormattedMessage {...messages.descriptionColumnLabel} /></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {parsed.rows.slice(0, 50).map((r) => (
                                        <TableRow key={r.name}>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell>{r.description}</TableCell>
                                        </TableRow>
                                    ))}
                                    {parsed.rows.length > 50 && (
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ opacity: 0.7 }}>
                                                <FormattedMessage {...messages.showsOnlyFirst50Rows} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button variant="contained" onClick={onClose}><FormattedMessage {...messages.cancel} /></Button>
                <Button
                    variant="contained"
                    onClick={handleImport}
                    disabled={isImporting || parsed.rows.length === 0 || parsed.errors.length > 0}
                >
                    <FormattedMessage {...messages.import} />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
